import type { ReactNode } from "react";
import { Redirect } from "wouter";
import { useUser } from "@/hooks/useUser";
import { PageLoader } from "@/components/ui/spinner";
import { RequireAuth } from "@/components/auth/RequireAuth";

interface RequireHostProps {
  children: ReactNode;
  /**
   * Where to send a signed-in non-host. Defaults to the marketplace home.
   * Kept as a prop so the (rare) "becoming a host" pages can opt to land
   * the user on /list-your-car instead.
   */
  fallbackPath?: string;
  /**
   * Opt-out gate for the listing wizard / onboarding pages. When set,
   * the host check is bypassed entirely so a non-host can complete the
   * wizard that *makes* them a host. Kept as an explicit prop (rather
   * than calling `RequireAuth` directly at the call-site) so every
   * dashboard route runs through this single wrapper — making it
   * impossible to forget the host gate when adding a new route in the
   * future.
   */
  allowListingWizard?: boolean;
}

/**
 * Synchronous host-only gate for the host dashboard. Wraps RequireAuth so
 * unauthenticated visitors get the same modal/redirect treatment, then
 * adds a hard role check before the child page mounts.
 *
 * Why this exists (#1752 defect 3): without it, every `/dashboard/*` route
 * was guarded only by `RequireAuth`, and the host-vs-renter check was
 * deferred to `ModeRouteSync` — an effect that fires *after* the dashboard
 * page has already rendered. A brand-new user (no listings, `is_host`
 * false) would briefly see the host dashboard chrome before being bounced
 * to the home page; mid-render queries to host-only endpoints (e.g.
 * /api/listings/my) would also fire and fail with 403s. RequireHost moves
 * that decision before the first render, removes the flash, and prevents
 * the wasteful API calls.
 *
 * Listing-wizard / listing-edit / onboarding pages intentionally do NOT
 * use this wrapper — those are the *transition* pages that turn a renter
 * into a host, so they must be reachable while `is_host` is still false.
 */
export function RequireHost({ children, fallbackPath = "/", allowListingWizard = false }: RequireHostProps) {
  return (
    <RequireAuth>
      {allowListingWizard ? <>{children}</> : <HostOnly fallbackPath={fallbackPath}>{children}</HostOnly>}
    </RequireAuth>
  );
}

function HostOnly({ children, fallbackPath }: { children: ReactNode; fallbackPath: string }) {
  const { user, profile, isLoading } = useUser();

  // RequireAuth has already guaranteed user + non-null profile by the
  // time this renders; the explicit guards below are defensive against a
  // future RequireAuth refactor that loosens those preconditions.
  if (isLoading) return <PageLoader className="min-h-[60vh]" />;
  if (!user || !profile) return <PageLoader className="min-h-[60vh]" />;

  const isHost = !!(profile as { is_host?: boolean }).is_host;
  if (!isHost) {
    return <Redirect to={fallbackPath} />;
  }
  return <>{children}</>;
}
